<template>
  <div id="pallets-list">
    <div class="pallets-list-wrapper" :class="{ 'pallets-list-wrapper-errors': showErrors }">
      <pallet-errors
        :class="{ hide: !showErrors }"
        :pallets="pallets"
        @errorClicked="scrollToPallet"
        @errorsUpdated="switchErrors"
      ></pallet-errors>
      <div class="panel-scrollable">
        <v-card
          :id="`palletCard${pallet.id}`"
          class="pallet-card"
          :class="[{ 'pallet-card-errors': showErrors }, { 'pallet-card-loaded': pallet.truck_id != null}]"
          v-for="(pallet, index) in pallets"
          :key="index"
          @click="selectPallet(pallet, index)"
          @dblclick="selectAndOpenPallet(pallet, index)"
        >
          <div class="pallet-header" :class="{ selected: index == showingPalletIndex }">
            <input
              type="checkbox"
              class="pallet-select-checkbox pallet-checkbox"
              :disabled="pallet.temporary"
              @input="checkPallet($event, pallet)"
            />
            <span v-if="pallet.number == null">
              {{
              pallet.toString(`${$t("pallets_list.pallet")} ${index + 1}`, user)
              }}
            </span>
            <span v-else>
              {{
              pallet.toString($t("pallets_list.pallet"), user)
              }}
            </span>
            <div
              class="view-pallet-3d"
              :class="{ 'view-pallet-3d-right': pallet.truck_id != null, 'read-only': user.type == 6, 'view-pallet-3d-fictive': !pallet.owned}"
              @click="openPalletViewing(pallet)"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="white">3d_rotation</v-icon>
                </template>
                <span>{{ $t("pallets_list.preview_pallet") }}</span>
              </v-tooltip>
            </div>
            <div
              @click="openChangeNumber(pallet)"
              class="change-pallet-number"
              v-if="user.company_id == 7 && user.type != 6 && pallet.owned && pallet.number && pallet.truck_id == null"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="white">qr_code</v-icon>
                </template>
                <span>{{ $t("pallets_list.change_number") }}</span>
              </v-tooltip>
            </div>
            <div
              class="edit-pallet-settings-button"
              v-if="!pallet.temporary && pallet.truck_id == null && pallet.owned && user.type != 6"
              @click="openPalletSettings(pallet)"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="white">settings</v-icon>
                </template>
                <span>{{ $t("pallets_list.edit_pallet") }}</span>
              </v-tooltip>
            </div>
            <div
              class="products-flip-button"
              v-if="!pallet.temporary && pallet.truck_id == null && pallet.owned && user.type != 6"
              @click="flipProducts()"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="white">replay</v-icon>
                </template>
                <span>{{ $t("pallets_list.flip_products") }}</span>
              </v-tooltip>
            </div>
            <div
              v-if="pallet.truck_id == null && user.type != 6"
              class="pallet-remove-button"
              @click="removePalletPrompt(pallet)"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="white">close</v-icon>
                </template>
                <span>{{ $t("pallets_list.remove_pallet") }}</span>
              </v-tooltip>
            </div>
          </div>
          <div class="pallet-view">
            <div class="pallet-top-wrapper">
              <pallet-top
                class="pallet-top-view"
                :pallet="pallet"
                :divider="getDividerTop(pallet)"
                :showPicture="true"
                :showErrors="true"
                @productSelected="showProductImage"
                @hideProduct="hideProduct"
              ></pallet-top>
            </div>
            <div class="pallet-side-wrapper">
              <pallet-side
                class="pallet-side-view"
                :pallet="pallet"
                :divider="getDividerSide(pallet)"
              ></pallet-side>
            </div>
          </div>
          <div class="pallet-products-list" v-if="pallet.owned">
            <div class="select-all">
              <input class="select-all-checkbox" type="checkbox" @input="flipAll($event, pallet)" />
              <span>{{ $t("pallets_list.all") }}</span>
            </div>
            <draggable
              :list="pallet.products"
              :disabled="!enabled"
              ghost-class="ghost"
              @change="moveProduct($event)"
              group="a"
            >
              <div
                class
                v-for="product in pallet.products"
                :key="product.id"
                @mouseover="changeSelected(product)"
                @mouseout="mouseIsOver = -1"
              >
                <input
                  v-if="product.can_be_flipped"
                  :id="'product-checkbox' + product.id"
                  class="product-checkbox"
                  type="checkbox"
                  @click.stop="flipProduct($event, product)"
                />
                <span>{{ product.getTextOnPallet(pallet, this) }}</span>
              </div>
            </draggable>
          </div>
        </v-card>
      </div>
    </div>
    <ConfirmBox ref="confirmBox" @confirmDialog="dialogConfirmed" />
  </div>
</template>

<script>
import PalletTop from "@/components/user/pallets/PalletTop.vue";
import PalletSide from "@/components/user/pallets/PalletSide.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import draggable from "vuedraggable";
import Product from "@/models/Product.js";
import PalletErrors from "@/components/palleting/PalletErrors.vue";
export default {
  name: "pallets-list",
  props: ["pallets", "showPallet", "showingPalletIndex", "client", "user"],
  data() {
    return {
      enabled: true,
      flippingProducts: [],
      //product image preview dependancies
      selectedProduct: null,
      mouseIsOver: -1,
      showErrors: false
    };
  },
  watch: {
    mouseIsOver(val) {
      if (val == -1 && this.selectedProduct != null) {
        this.selectedProduct = null;
        this.$emit("hideProduct");
      }
    }
  },
  methods: {
    getDividerTop(pallet) {
      //get first (same size so doesnt matter)
      const wrapper = document.getElementsByClassName("pallet-top-wrapper")[0];
      if (!wrapper) {
        return 10;
      }
      let divider = pallet.length / (wrapper.offsetWidth - 10);
      if (divider < 10) {
        return 12;
      }
      return divider;
    },
    getDividerSide(pallet) {
      const wrapper = document.getElementsByClassName("pallet-side-wrapper")[1];
      if (!wrapper) {
        return 24;
      }
      let divider = pallet.getHeight() / (wrapper.offsetHeight - 60);
      if (divider < 12) {
        return 12;
      }
      return divider;
    },
    changeShowPallet() {
      this.$emit("switchView");
    },
    selectPallet(pallet, index) {
      if (pallet.temporary) return;
      this.$emit("palletSelected", index);
    },
    async selectAndOpenPallet(pallet, index) {
      if (pallet.temporary) return;
      this.$emit("openPallet");
      this.selectPallet(pallet, index);
    },
    openPalletViewing(pallet) {
      this.$emit("openPalletViewing", pallet);
    },
    openPalletSettings(pallet) {
      this.$emit("openPalletEdittor", [pallet]);
    },
    openChangeNumber(pallet) {
      this.$emit("changeNumber", pallet);
    },
    removePalletPrompt(pallet) {
      if (pallet.owned) {
        if (pallet.status >= 2) {
          alert(this.$t("pallets_list.already_confirmed"));
          return;
        }
      }
      const confirmAction = {
        function: "removePallet",
        pallet: pallet
      };
      this.$refs.confirmBox.openDialog(
        this.$t("pallets_list.confirm_delete"),
        confirmAction
      );
    },
    dialogConfirmed(confirmation) {
      this[confirmation.function](confirmation.pallet);
    },
    async removePallet(pallet) {
      if (!pallet.temporary) {
        this.$store.commit("setLoading", true);
        await pallet.remove();
        this.$emit("updated");
      } else {
        this.$emit("removeTemporary");
      }
    },
    async moveProduct($event) {
      const pallets = this.pallets.slice();
      let createdPallet = pallets.find(e => {
        return e.temporary;
      });
      this.$store.commit("setLoading", true);
      const tempPallet = await Product.moveToOtherPallet(
        $event,
        pallets,
        this.showingPalletIndex,
        this.$t("pallets_list.already_confirmed")
      );
      if (tempPallet) this.$emit("palletCreated", tempPallet);
      if (createdPallet) this.$emit("removeTemporary");
      this.$emit("updated");
    },
    flipAll(event, pallet) {
      pallet.products.forEach(product => {
        if (product.can_be_flipped) {
          this.flipProduct(event, product);
          document.getElementById(`product-checkbox${product.id}`).checked =
            event.target.checked;
        }
      });
    },
    flipProduct(event, product) {
      if (event.target.checked) {
        //if checked add product to flipping products
        this.flippingProducts.push(product);
      } else {
        //else remove it
        const foundIndex = this.flippingProducts.indexOf(product);
        if (foundIndex != -1) {
          this.flippingProducts.splice(foundIndex, 1);
        }
      }
    },
    checkAllPallets(event) {
      const inputs = [].slice.call(
        document.getElementsByClassName("pallet-select-checkbox")
      );
      for (let i = 0; i < this.pallets.length; i++) {
        if (/*!this.pallets[i].truck_id && */ !this.pallets[i].temporary) {
          this.checkPallet(event, this.pallets[i]);
          inputs[i].checked = event.target.checked;
        }
      }
    },
    uncheckAllPallets() {
      //uncheck check all input
      document.getElementById("check-all-checkbox").checked = false;
      //uncheck all pallet inputs
      const inputs = [].slice.call(
        document.getElementsByClassName("pallet-select-checkbox")
      );
      inputs.forEach(input => {
        input.checked = false;
      });
    },
    checkPallet(event, pallet) {
      if (event.target.checked) {
        this.$emit("palletChecked", pallet);
      } else {
        this.$emit("palletUnchecked", pallet);
      }
    },
    async flipProducts() {
      if (this.flippingProducts.length < 1) return;
      const inputsForAll = [].slice.call(
        document.getElementsByClassName("select-all-checkbox")
      );
      const inputs = [].slice.call(
        document.getElementsByClassName("product-checkbox")
      );
      //uncheck checkboxes
      inputsForAll.forEach(input => {
        input.checked = false;
      });
      inputs.forEach(input => {
        input.checked = false;
      });
      const pallet = this.pallets.find(e => {
        return e.id == this.flippingProducts[0].pallet_id;
      });
      if (pallet.status >= 2) {
        alert(this.$t("pallets_list.already_confirmed"));
        return;
      }
      this.$store.commit("setLoading", true);
      await Product.flipProducts(this.flippingProducts);
      this.flippingProducts = [];
      this.$emit("updated");
    },
    showProductImage(product) {
      this.$emit("productSelected", product);
    },
    hideProduct() {
      this.$emit("hideProduct");
    },
    changeSelected(product) {
      product.select(this);
    },
    scrollToPallet(pallet) {
      const palletCard = document.getElementById(`palletCard${pallet.id}`);
      if (palletCard) {
        palletCard.scrollIntoView();
        palletCard.click();
      }
    },
    switchErrors(show) {
      this.showErrors = show;
    }
  },
  components: {
    PalletTop,
    PalletSide,
    draggable,
    ConfirmBox,
    PalletErrors
  }
};
</script>

<style scoped>
.pallets-list-wrapper {
  position: absolute;
  width: 100%;
  height: calc(100%);
}
.pallets-list-wrapper-errors {
  height: calc(100% - 240px);
}
.pallet-card {
  position: relative;
  display: table;
  width: calc(100% - 50px);
  min-height: 200px;
  margin: 0 auto;
  margin-top: 20px;
}
.pallet-card-loaded {
  background: rgba(26, 27, 26, 0.1) !important;
}
.pallet-card-errors {
  margin-top: 0px;
  margin-bottom: 20px;
}
.pallet-header {
  position: relative;
  padding-left: 10px;
  padding-top: 2px;
  width: 100%;
  height: 25px;
  font-size: 14px;
  font-weight: 500;
}
.pallet-view {
  position: absolute;
  width: calc(100% - 300px);
  height: calc(100% - 30px);
}
.pallet-top-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% - 120px);
  height: 100%;
  min-width: 150px;
}
.pallet-top-view {
  margin-top: 10px;
  margin-left: 10px;
}
.pallet-side-wrapper {
  position: absolute;
  left: calc(100% - 120px);
  width: 120px;
  height: 100%;
}
.pallet-side-view {
  margin-top: 10px;
  margin-left: 10px;
}
.pallet-products-list {
  padding-top: 10px;
  padding-left: 10px;
  position: relative;
  float: right;
  width: 300px;
}
.product-info {
  font-size: 16px;
}
.view-changer {
  position: absolute;
  right: 0;
  top: 0;
}
.selected {
  background: rgb(139, 212, 71);
}
.pallet-remove-button {
  cursor: pointer;
  background: rgb(255, 50, 50);
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  height: 25px;
  text-align: center;
  vertical-align: middle;
}
.products-flip-button {
  cursor: pointer;
  background: #4a148c;
  position: absolute;
  top: 0;
  right: 50px;
  height: 25px;
  width: 50px;
  text-align: center;
  vertical-align: middle;
}
.view-pallet-3d {
  cursor: pointer;
  position: absolute;
  background: #5c6bc0;
  top: 0;
  right: 150px;
  width: 50px;
  height: 25px;
  text-align: center;
  vertical-align: middle;
}
.view-pallet-3d-right {
  right: 0px;
}
.view-pallet-3d-fictive
{
  right: 50px
}
.edit-pallet-settings-button {
  cursor: pointer;
  position: absolute;
  background: #42a5f5;
  top: 0;
  right: 100px;
  width: 50px;
  height: 25px;
  text-align: center;
  vertical-align: middle;
}
.change-pallet-number {
  cursor: pointer;
  position: absolute;
  background: #beae1d;
  top: 0;
  right: 200px;
  width: 50px;
  height: 25px;
  text-align: center;
  vertical-align: middle;
}
.select-all {
  font-weight: 700;
  border-bottom: solid;
}
.pallet-checkbox {
  width: 25px;
  height: 20px;
}
@media only screen and (max-width: 1500px) {
  .pallet-side-wrapper {
    display: none;
  }
}
.read-only {
  right: 0;
}
</style>
